import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, Minus, Plus, ShoppingBag, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { toast } from 'sonner';
import ProductCard from '@/components/ProductCard';
import ProductReviews from '@/components/ProductReviews';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorited, setIsFavorited] = useState(false);
  const [addingToCart, setAddingToCart] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/products/${id}`);
        setProduct(response.data);
        setSelectedSize(response.data.sizes[0] || '');
        setSelectedColor(response.data.colors[0] || '');
        
        // Fetch related products
        const relatedRes = await axios.get(`${API}/products?category=${response.data.category}`);
        setRelatedProducts(relatedRes.data.filter(p => p.id !== id).slice(0, 4));
        
        // Check if favorited
        if (isAuthenticated) {
          try {
            const favRes = await axios.get(`${API}/favorites/check/${id}`);
            setIsFavorited(favRes.data.favorited);
          } catch {}
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Produto não encontrado');
        navigate('/shop');
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id, isAuthenticated, navigate]);

  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      toast.error('Faça login para adicionar ao carrinho');
      navigate('/auth');
      return;
    }

    if (!selectedSize || !selectedColor) {
      toast.error('Selecione tamanho e cor');
      return;
    }

    setAddingToCart(true);
    try {
      await addToCart(product.id, quantity, selectedSize, selectedColor);
      toast.success('Adicionado ao carrinho!');
    } catch (error) {
      toast.error('Erro ao adicionar ao carrinho');
    } finally {
      setAddingToCart(false);
    }
  };

  const toggleFavorite = async () => {
    if (!isAuthenticated) {
      toast.error('Faça login para adicionar aos favoritos');
      return;
    }

    try {
      const response = await axios.post(`${API}/favorites/${product.id}`);
      setIsFavorited(response.data.favorited);
      toast.success(response.data.favorited ? 'Adicionado aos favoritos' : 'Removido dos favoritos');
    } catch (error) {
      toast.error('Erro ao atualizar favoritos');
    }
  };

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === product.images.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? product.images.length - 1 : prev - 1
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24">
        <div className="container-luxury py-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div className="aspect-[3/4] skeleton" />
            <div className="space-y-6">
              <div className="h-8 w-3/4 skeleton" />
              <div className="h-6 w-1/4 skeleton" />
              <div className="h-24 skeleton" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen pt-24" data-testid="product-detail-page">
      <div className="container-luxury py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div className="space-y-4">
            <div className="relative aspect-[3/4] bg-card overflow-hidden group">
              <img
                src={product.images[currentImageIndex]}
                alt={product.name}
                className="w-full h-full object-cover"
                data-testid="main-product-image"
              />
              
              {product.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-background/80 backdrop-blur-sm hover:bg-primary hover:text-primary-foreground transition-all opacity-0 group-hover:opacity-100"
                    data-testid="prev-image-btn"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-background/80 backdrop-blur-sm hover:bg-primary hover:text-primary-foreground transition-all opacity-0 group-hover:opacity-100"
                    data-testid="next-image-btn"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}

              {product.featured && (
                <span className="absolute top-4 left-4 px-3 py-1 bg-primary text-primary-foreground text-xs uppercase tracking-wider">
                  Destaque
                </span>
              )}
            </div>

            {/* Thumbnails */}
            {product.images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`w-20 h-24 flex-shrink-0 border transition-all ${
                      idx === currentImageIndex 
                        ? 'border-primary' 
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-8">
            <div>
              <p className="text-accent-italic text-primary mb-2">{product.category}</p>
              <h1 className="font-serif text-4xl mb-4" data-testid="product-name">
                {product.name}
              </h1>
              <p className="text-3xl text-primary font-bold" data-testid="product-price">
                ${product.price.toFixed(2)}
              </p>
            </div>

            <p className="text-muted-foreground leading-relaxed" data-testid="product-description">
              {product.description}
            </p>

            {/* Size Selection */}
            <div>
              <h4 className="text-sm uppercase tracking-wider font-bold mb-4">Tamanho</h4>
              <div className="flex flex-wrap gap-3">
                {product.sizes.map((size) => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`size-btn ${selectedSize === size ? 'selected' : ''}`}
                    data-testid={`size-${size}`}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>

            {/* Color Selection */}
            <div>
              <h4 className="text-sm uppercase tracking-wider font-bold mb-4">Cor</h4>
              <div className="flex flex-wrap gap-3">
                {product.colors.map((color) => (
                  <button
                    key={color}
                    onClick={() => setSelectedColor(color)}
                    className={`px-4 py-2 border text-sm transition-all ${
                      selectedColor === color 
                        ? 'border-primary text-primary' 
                        : 'border-white/20 text-muted-foreground hover:border-white/40'
                    }`}
                    data-testid={`color-${color}`}
                  >
                    {color}
                  </button>
                ))}
              </div>
            </div>

            {/* Quantity */}
            <div>
              <h4 className="text-sm uppercase tracking-wider font-bold mb-4">Quantidade</h4>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="qty-btn"
                  data-testid="qty-decrease"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-12 text-center text-lg" data-testid="quantity-display">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="qty-btn"
                  data-testid="qty-increase"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-4 pt-4">
              <Button
                onClick={handleAddToCart}
                disabled={addingToCart}
                className="btn-primary flex-1"
                data-testid="add-to-cart-btn"
              >
                {addingToCart ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                    Adicionando...
                  </span>
                ) : (
                  <>
                    <ShoppingBag className="w-4 h-4 mr-2" />
                    Adicionar ao Carrinho
                  </>
                )}
              </Button>
              <Button
                onClick={toggleFavorite}
                variant="outline"
                className={`btn-outline px-4 ${isFavorited ? 'bg-primary text-primary-foreground' : ''}`}
                data-testid="favorite-btn"
              >
                <Heart className={`w-5 h-5 ${isFavorited ? 'fill-current' : ''}`} />
              </Button>
            </div>

            {/* Stock */}
            <p className="text-sm text-muted-foreground">
              {product.stock > 0 ? (
                <span className="text-green-500">✓ Em estoque ({product.stock} disponíveis)</span>
              ) : (
                <span className="text-red-500">Fora de estoque</span>
              )}
            </p>
          </div>
        </div>

        {/* Reviews Section */}
        <ProductReviews productId={id} />

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="mt-24">
            <h2 className="font-serif text-3xl mb-8">Você Também Pode Gostar</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {relatedProducts.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
